import { formatArea, formatLength, formatMoney } from '../quote/format';
import type { QuoteResult, RoomInput } from '../quote/types';
import {
  checkCard,
  copy,
  countField,
  escapeHtml,
  numericField,
  option,
  radioCard,
  roomLabel,
  roomTypes,
  state
} from './model';

export function renderProjectStep(): string {
  const c = copy();
  const uk = state.market === 'UK';
  return `
    <section class="step-panel" aria-labelledby="step-title">
      <div class="step-heading"><p>${c.step} 1 ${c.of} 5</p><h2 id="step-title">${c.project}</h2><span>${c.projectIntro}</span></div>
      <div class="section-block">
        <h3>${c.marketLabel}</h3>
        <div class="choice-grid two">
          ${radioCard('market', 'UK', c.marketUK, 'GBP · EN-GB', uk, 'data-action="market"')}
          ${radioCard('market', 'PL', c.marketPL, 'PLN · PL-PL', !uk, 'data-action="market"')}
        </div>
      </div>
      <div class="section-block">
        <h3>${c.propertyType}</h3>
        <div class="choice-grid three">
          ${radioCard('propertyType', 'house', c.propertyHouse, '', state.propertyType === 'house', 'data-action="property-type"')}
          ${radioCard('propertyType', 'flat', c.propertyFlat, '', state.propertyType === 'flat', 'data-action="property-type"')}
          ${radioCard('propertyType', 'commercial', c.propertyCommercial, '', state.propertyType === 'commercial', 'data-action="property-type"')}
        </div>
      </div>
      <div class="section-block split-block">
        <div>
          <h3>${c.region}</h3>
          <div class="choice-stack">
            ${uk
              ? `${radioCard('region', 'standard', c.regionStandardUK, '', state.region === 'standard', 'data-action="region"')}${radioCard('region', 'londonSouthEast', c.regionLondon, '+25%', state.region === 'londonSouthEast', 'data-action="region"')}`
              : `${radioCard('region', 'standard', c.regionStandardPL, '', state.region === 'standard', 'data-action="region"')}${radioCard('region', 'warsaw', c.regionWarsaw, '+18%', state.region === 'warsaw', 'data-action="region"')}`}
          </div>
        </div>
        <div>
          <h3>${c.travel}</h3>
          <div class="choice-stack">
            ${radioCard('travel', 'local', c.travelLocal, '', state.travel === 'local', 'data-action="travel"')}
            ${radioCard('travel', 'extended', c.travelExtended, uk ? '+£45' : '+150 zł', state.travel === 'extended', 'data-action="travel"')}
            ${radioCard('travel', 'outside', c.travelOutside, c.noAutomaticPrice, state.travel === 'outside', 'data-action="travel"')}
          </div>
        </div>
      </div>
    </section>`;
}

function roomHeader(room: RoomInput, index: number): string {
  const c = copy();
  return `
    <div class="room-header">
      <div><span>${c.room} ${index + 1}</span><h3>${escapeHtml(roomLabel(room, index))}</h3></div>
      <div class="room-actions">
        <button type="button" class="text-button" data-action="duplicate-room" data-room-id="${room.id}">${c.duplicateRoom}</button>
        ${state.rooms.length > 1 ? `<button type="button" class="text-button danger" data-action="remove-room" data-room-id="${room.id}">${c.removeRoom}</button>` : ''}
      </div>
    </div>`;
}

export function renderDimensionsStep(): string {
  const c = copy();
  return `
    <section class="step-panel" aria-labelledby="step-title">
      <div class="step-heading"><p>${c.step} 2 ${c.of} 5</p><h2 id="step-title">${c.dimensions}</h2><span>${c.dimensionsHelp}</span></div>
      <div class="rooms-list">
        ${state.rooms.map((room, index) => `
          <article class="room-card">
            ${roomHeader(room, index)}
            <div class="field-grid two">
              <div class="field">
                <label for="${room.id}-type">${c.roomType}</label>
                <select id="${room.id}-type" data-room-id="${room.id}" data-field="type">
                  ${roomTypes.map((type) => {
                    const key = `type${type.charAt(0).toUpperCase()}${type.slice(1)}` as keyof typeof c;
                    return option(type, String(c[key]), room.type === type);
                  }).join('')}
                </select>
              </div>
              <div class="field">
                <label for="${room.id}-customName">${c.customName}</label>
                <input id="${room.id}-customName" type="text" maxlength="40" value="${escapeHtml(room.customName)}" data-room-id="${room.id}" data-field="customName" />
              </div>
            </div>
            <div class="field-grid three dimension-grid">
              ${numericField(room, 'length', c.length, 1.5, 15, 0.1, 'm')}
              ${numericField(room, 'width', c.width, 1.5, 15, 0.1, 'm')}
              ${numericField(room, 'height', c.height, 2, 5, 0.1, 'm')}
            </div>
            <div class="field-grid three">
              ${countField(room, 'doors', c.doors, 10)}
              ${countField(room, 'windows', c.windows, 15)}
              ${countField(room, 'radiators', c.radiators, 10)}
            </div>
          </article>`).join('')}
      </div>
      <button type="button" class="add-room" data-action="add-room" ${state.rooms.length >= 5 ? 'disabled' : ''}>＋ ${c.addRoom}</button>
      ${state.rooms.length >= 5 ? `<p class="limit-note">${c.maximumRooms}</p>` : ''}
    </section>`;
}

export function renderSurfacesStep(): string {
  const c = copy();
  return `
    <section class="step-panel" aria-labelledby="step-title">
      <div class="step-heading"><p>${c.step} 3 ${c.of} 5</p><h2 id="step-title">${c.surfaces}</h2><span>${c.selectSurface}</span></div>
      <div class="rooms-list">
        ${state.rooms.map((room, index) => {
          const surfaceError = state.errors.get(`${room.id}:surfaces`);
          return `
            <article class="room-card ${surfaceError ? 'room-error' : ''}">
              ${roomHeader(room, index)}
              <div class="check-grid three">
                ${checkCard(c.paintWalls, room.paintWalls, `data-room-id="${room.id}" data-field="paintWalls"`)}
                ${checkCard(c.paintCeiling, room.paintCeiling, `data-room-id="${room.id}" data-field="paintCeiling"`)}
                ${checkCard(c.paintSkirting, room.paintSkirting, `data-room-id="${room.id}" data-field="paintSkirting"`)}
              </div>
              ${surfaceError ? `<p class="field-error room-level-error">${escapeHtml(surfaceError)}</p>` : ''}
              <div class="field-grid two surface-counts">
                ${countField(room, 'paintedDoors', c.paintedDoors, 10)}
                ${countField(room, 'paintedRadiators', c.paintedRadiators, 10)}
              </div>
              <div class="section-divider"></div>
              <h4>${c.colourChange}</h4>
              <div class="choice-grid three">
                ${radioCard(`colour-${room.id}`, 'similar', c.colourSimilar, '', room.colourChange === 'similar', `data-room-id="${room.id}" data-field="colourChange"`)}
                ${radioCard(`colour-${room.id}`, 'noticeable', c.colourNoticeable, '+8%', room.colourChange === 'noticeable', `data-room-id="${room.id}" data-field="colourChange"`)}
                ${radioCard(`colour-${room.id}`, 'darkToLight', c.colourDarkToLight, '+15%', room.colourChange === 'darkToLight', `data-room-id="${room.id}" data-field="colourChange"`)}
              </div>
            </article>`;
        }).join('')}
      </div>
    </section>`;
}

export function renderConditionStep(): string {
  const c = copy();
  const conditionOptions = [
    ['good', c.conditionGood, c.conditionGoodHelp],
    ['minorRepairs', c.conditionMinor, c.conditionMinorHelp],
    ['noticeableRepairs', c.conditionNoticeable, c.conditionNoticeableHelp],
    ['newPlaster', c.conditionNewPlaster, c.conditionNewPlasterHelp],
    ['specialist', c.conditionSpecialist, c.conditionSpecialistHelp]
  ] as const;

  return `
    <section class="step-panel" aria-labelledby="step-title">
      <div class="step-heading"><p>${c.step} 4 ${c.of} 5</p><h2 id="step-title">${c.condition}</h2><span>${c.conditionGoodHelp}</span></div>
      <div class="rooms-list">
        ${state.rooms.map((room, index) => `
          <article class="room-card">
            ${roomHeader(room, index)}
            <h4>${c.conditionLabel}</h4>
            <div class="choice-grid condition-grid">
              ${conditionOptions.map(([value, label, description]) => radioCard(`condition-${room.id}`, value, label, description, room.condition === value, `data-room-id="${room.id}" data-field="condition"`)).join('')}
            </div>
            <div class="check-grid two extras-grid">
              ${checkCard(c.wallpaperRemoval, room.wallpaperRemoval, `data-room-id="${room.id}" data-field="wallpaperRemoval"`)}
              ${checkCard(c.stainBlocking, room.stainBlocking, `data-room-id="${room.id}" data-field="stainBlocking"`)}
              ${checkCard(c.highCeiling, room.highCeiling, `data-room-id="${room.id}" data-field="highCeiling"`)}
              ${checkCard(c.difficultAccess, room.difficultAccess, `data-room-id="${room.id}" data-field="difficultAccess"`)}
              ${checkCard(c.furnished, room.furnished, `data-room-id="${room.id}" data-field="furnished"`)}
            </div>
            <details class="risk-details">
              <summary>${c.risksTitle}</summary>
              <div class="check-grid two">
                ${checkCard(c.moistureOrMould, room.issues.moistureOrMould, `data-room-id="${room.id}" data-issue="moistureOrMould"`)}
                ${checkCard(c.unstableSurface, room.issues.unstableSurface, `data-room-id="${room.id}" data-issue="unstableSurface"`)}
                ${checkCard(c.scaffoldingRequired, room.issues.scaffoldingRequired, `data-room-id="${room.id}" data-issue="scaffoldingRequired"`)}
                ${checkCard(c.specialistFinish, room.issues.specialistFinish, `data-room-id="${room.id}" data-issue="specialistFinish"`)}
                ${checkCard(c.fullReplastering, room.issues.fullReplastering, `data-room-id="${room.id}" data-issue="fullReplastering"`)}
                ${checkCard(c.exteriorWork, room.issues.exteriorWork, `data-room-id="${room.id}" data-issue="exteriorWork"`)}
              </div>
            </details>
          </article>`).join('')}
      </div>
    </section>`;
}

function selectedWork(room: RoomInput): string[] {
  const c = copy();
  const values: string[] = [];
  if (room.paintWalls) values.push(c.paintWalls);
  if (room.paintCeiling) values.push(c.paintCeiling);
  if (room.paintSkirting) values.push(c.paintSkirting);
  if (room.paintedDoors) values.push(`${c.paintedDoors}: ${room.paintedDoors}`);
  if (room.paintedRadiators) values.push(`${c.paintedRadiators}: ${room.paintedRadiators}`);
  return values;
}

function renderRoomSummary(room: RoomInput, index: number, result?: QuoteResult): string {
  const c = copy();
  const breakdown = result?.rooms.find((entry) => entry.roomId === room.id);
  return `
    <article class="summary-room">
      <div class="summary-room-head"><div><span>${c.room} ${index + 1}</span><h4>${escapeHtml(roomLabel(room, index))}</h4></div><strong>${room.length} × ${room.width} × ${room.height} m</strong></div>
      <div class="summary-columns">
        <div><small>${c.surfacesSummary}</small><p>${selectedWork(room).map(escapeHtml).join(' · ')}</p></div>
        ${breakdown ? `<div><small>${c.geometrySummary}</small><p>${c.wallsArea}: ${formatArea(breakdown.geometry.wallArea, state.locale)} · ${c.ceilingArea}: ${formatArea(breakdown.geometry.ceilingArea, state.locale)} · ${c.skirtingLength}: ${formatLength(breakdown.geometry.skirtingLength, state.locale)}</p></div>` : ''}
      </div>
    </article>`;
}

function renderResult(result: QuoteResult): string {
  const c = copy();
  if (result.status === 'manual') {
    return `
      <section class="result-card manual" aria-live="polite">
        <div class="result-icon" aria-hidden="true">!</div>
        <p class="eyebrow">${c.noAutomaticPrice}</p>
        <h3>${c.manualTitle}</h3>
        <p>${c.manualBody}</p>
        <ul>${result.manualReasons.map((reason) => {
          const label = c.reasonLabels[reason.code as keyof typeof c.reasonLabels] ?? reason.code;
          return `<li>${escapeHtml(label)}</li>`;
        }).join('')}</ul>
      </section>`;
  }

  return `
    <section class="result-card estimate" aria-live="polite">
      <p class="eyebrow">${c.estimateTitle}</p>
      <div class="price-range">
        <span>${c.estimateRange}</span>
        <strong>${formatMoney(result.lowEstimate ?? 0, result, state.locale)}–${formatMoney(result.highEstimate ?? 0, result, state.locale)}</strong>
      </div>
      <div class="central-value"><span>${c.estimateCentral}</span><b>${formatMoney(result.centralEstimate ?? 0, result, state.locale)}</b></div>
    </section>`;
}

export function renderFinishStep(): string {
  const c = copy();
  return `
    <section class="step-panel" aria-labelledby="step-title">
      <div class="step-heading"><p>${c.step} 5 ${c.of} 5</p><h2 id="step-title">${state.result ? c.result : c.finish}</h2><span>${state.result ? c.disclaimer : c.summarySubtitle}</span></div>
      <div class="section-block">
        <h3>${c.materialStandard}</h3>
        <div class="choice-grid two">
          ${radioCard('materials', 'standard', c.materialStandardValue, '', state.rooms.every((room) => room.materialStandard === 'standard'), 'data-action="materials"')}
          ${radioCard('materials', 'premium', c.materialPremium, '+15%', state.rooms.every((room) => room.materialStandard === 'premium'), 'data-action="materials"')}
        </div>
      </div>
      ${state.result ? renderResult(state.result) : ''}
      <div class="summary-list">
        <div class="summary-title"><h3>${c.summaryTitle}</h3><p>${c.summarySubtitle}</p></div>
        ${state.rooms.map((room, index) => renderRoomSummary(room, index, state.result ?? undefined)).join('')}
      </div>
      ${state.result ? `
        <div class="disclaimer"><strong>${c.conceptLabel}</strong><p>${c.disclaimer}</p></div>
        <div class="demo-cta"><div><h3>${c.conceptBody}</h3><p>${c.ctaNote}</p></div><a href="mailto:contact@mkprojectlab.com?subject=Quote%20calculator%20for%20my%20business" class="button primary">${c.cta}</a></div>
      ` : ''}
    </section>`;
}
